import { OrderedRow } from '../models/priceRows.model';
import { BUDGET_VARIABLE, evaluateFormula, formulaVariables, isValidFormula } from './formula';

export type RowValue = {
  amount?: number;
  unit?: string;
  problem?: 'circular' | 'divisionByZero' | 'brokenInput' | 'invalid';
};

export type RowPrice = { id: string; amount?: number; unit?: string };

type FormulaRow = Extract<OrderedRow, { kind: 'formula' }>;

export const computePriceRowValues = (rows: OrderedRow[], prices: RowPrice[], budget?: number): Map<string, RowValue> => {
  const rowById = new Map(rows.map(row => [row.id, row]));
  const priceById = new Map<string, RowPrice>();
  for (const price of prices) if (!priceById.has(price.id)) priceById.set(price.id, price);

  const referencesOf = new Map(rows.map(row => [row.id, row.kind === 'formula' ? formulaVariables(row.formula) : []]));
  const rowsUsedBy = (id: string) => {
    const reached = new Set<string>();
    const pending = [...(referencesOf.get(id) ?? [])];
    while (pending.length) {
      const next = pending.pop() as string;
      if (reached.has(next)) continue;
      reached.add(next);
      pending.push(...(referencesOf.get(next) ?? []));
    }
    return reached;
  };
  const used = new Map(rows.map(row => [row.id, rowsUsedBy(row.id)]));
  const uses = (row: OrderedRow, id: string) => !!used.get(row.id)?.has(id);

  const values = new Map<string, RowValue>();
  const valueOf = (row: OrderedRow): RowValue => {
    const known = values.get(row.id);
    if (known) return known;
    const value = calculate(row);
    values.set(row.id, value);
    return value;
  };

  const calculate = (row: OrderedRow): RowValue => {
    switch (row.kind) {
      case 'sum':
        return totalSum(row);
      case 'price':
        return supplierPrice(row);
      case 'value':
        return { amount: priceById.get(row.id)?.amount || undefined, unit: row.unit };
      case 'formula':
        return formulaValue(row);
      case 'text':
        return {};
    }
  };

  // A price of 0 counts as no price, matching how the bid overview shows it.
  const supplierPrice = (row: OrderedRow): RowValue => {
    const { amount, unit } = priceById.get(row.id) ?? {};
    return { amount: amount || undefined, unit };
  };

  const totalSum = (sumRow: OrderedRow): RowValue => {
    const rowsAbove = rows.slice(0, rows.indexOf(sumRow)).filter(row => row.kind !== 'sum' && !uses(row, sumRow.id));
    const unit = rowsAbove.map(row => valueOf(row).unit).find(Boolean);
    if (!unit) return {};

    let total: number | undefined;
    for (const row of rowsAbove) {
      const { amount, unit: rowUnit } = valueOf(row);
      if (rowUnit === unit && amount !== undefined) total = (total ?? 0) + amount;
    }
    return { amount: total, unit };
  };

  const variableValue = (variable: string): RowValue => {
    if (variable === BUDGET_VARIABLE) return { amount: budget };
    const referenced = rowById.get(variable);
    return referenced ? valueOf(referenced) : {};
  };

  // A missing value counts as 0, but a value that could not be calculated breaks the formula using it.
  const formulaValue = (row: FormulaRow): RowValue => {
    const { unit, formula } = row;
    if (uses(row, row.id)) return { unit, problem: 'circular' };

    const variables = referencesOf.get(row.id) ?? [];
    if (!isValidFormula(formula, variables)) return { unit, problem: 'invalid' };
    const inputs = new Map(variables.map(variable => [variable, variableValue(variable)]));
    if (Array.from(inputs.values()).some(input => input.problem)) return { unit, problem: 'brokenInput' };

    const amount = evaluateFormula(formula, variable => inputs.get(variable)?.amount ?? 0);
    return amount === undefined ? { unit, problem: 'divisionByZero' } : { amount, unit };
  };

  return new Map(rows.map(row => [row.id, valueOf(row)]));
};
